import { Close } from '@mui/icons-material'
import { Box, Button, Chip, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useStyles } from '../style'
import ConfirmDialog from '../../confirm-dialog'
import { useFormFactor } from '../../../hooks'

const CustomSkillInput = ({
  onAddSkill,
  customTopics,
  onRemoveSkill,
  allAvailableOptions,
  onSelectExistingSkill
}) => {
  const classes = useStyles()
  const { isMobile } = useFormFactor()
  const [isOtherInputActive, setIsOtherInputActive] = useState(false)
  const [otherInputValue, setOtherInputValue] = useState('')
  const [message, setMessage] = useState('')
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [skillToRemove, setSkillToRemove] = useState(null)

  const handleOtherButtonClick = () => {
    setIsOtherInputActive(!isOtherInputActive)
  }

  const handleOtherInputChange = e => {
    setOtherInputValue(e.target.value)
  }

  const handleOtherInputKeyPress = e => {
    if (e.key === 'Enter' && otherInputValue.trim()) {
      const trimmedValue = otherInputValue.trim()

      const topicExists = customTopics.some(
        skill => skill.name.toLowerCase() === trimmedValue.toLowerCase()
      )

      const existingTopic = allAvailableOptions.find(
        skill => skill.name.toLowerCase() === trimmedValue.toLowerCase()
      )

      if (existingTopic && !topicExists) {
        setMessage(`"${existingTopic.name}" is available and has been selected!`)
        onSelectExistingSkill(existingTopic.sk)
        setOtherInputValue('')
        setTimeout(() => setMessage(''), 3000)
      } else if (!topicExists && !existingTopic) {
        onAddSkill(trimmedValue)
        setOtherInputValue('')
        setMessage('')
      } else if (topicExists) {
        setMessage(`"${trimmedValue}" is already added`)
        setTimeout(() => setMessage(''), 3000)
      }
    }
  }

  const handleSkillKeyDown = (e, skill) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      setSkillToRemove(skill)
      setShowRemoveConfirm(true)
    }
  }

  const handleConfirmRemove = () => {
    if (skillToRemove) {
      onRemoveSkill(skillToRemove.sk)
      setSkillToRemove(null)
    }
    setShowRemoveConfirm(false)
  }

  const handleCancelRemove = () => {
    setSkillToRemove(null)
    setShowRemoveConfirm(false)
  }

  const handleRemoveClick = (skill) => {
    setSkillToRemove(skill)
    setShowRemoveConfirm(true)
  }

  return (
    <Box className={classes.otherInputContainer} data-test='custom-skill-input-container'>
      <Button
        className={`${classes.otherButton} ${isOtherInputActive ? 'selected' : ''}`}
        onClick={handleOtherButtonClick}
        data-test='custom-skill-add-button'
        aria-label={isOtherInputActive ? 'Hide custom skill input' : 'Add custom skill or topic'}
      >
        + Other
      </Button>

      {customTopics.length > 0 && (
        <Box className={classes.customTopicsDisplay} data-test='custom-topics-display'>
          {customTopics.map((skill, index) => (
            <Chip
              key={skill.sk || index}
              label={skill.name}
              className={classes.customItemChip}
              onDelete={() => handleRemoveClick(skill)}
              onKeyDown={(e) => handleSkillKeyDown(e, skill)}
              deleteIcon={<Close fontSize='small' />}
              aria-label={`Custom topic: ${skill.name}, click to remove or press space bar`}
              data-test={`custom-topic-chip-${skill.name.replace(/\s+/g, '-').toLowerCase()}`}
              tabIndex={0}
            />
          ))}
        </Box>
      )}

      {isOtherInputActive && (
        <Box className={classes.otherInputWrapper} data-test='other-input-wrapper'>
          <Typography className={classes.otherInputLabel}>
            Please specify (press Enter to add)
          </Typography>
          <TextField
            value={otherInputValue}
            onChange={handleOtherInputChange}
            onKeyPress={handleOtherInputKeyPress}
            className={classes.otherInputField}
            autoFocus
            inputProps={{
              maxLength: 100,
              'aria-label': 'Enter custom skill or topic name'
            }}
            data-test='other-input-field'
          />
          {message && (
            <Typography className={classes.messageText} data-test='input-message'>
              {message}
            </Typography>
          )}
        </Box>
      )}

      <ConfirmDialog
        name='remove-custom-skill'
        title='Remove custom skill?'
        description={`Are you sure you want to remove "${skillToRemove?.name}" from your custom skills?`}
        open={showRemoveConfirm}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        cancelText='Cancel'
        confirmText='Remove'
        variant='warning'
        DialogProps={{
          maxWidth: isMobile ? 'xs' : 'sm',
          fullWidth: true,
          PaperProps: {
            sx: {
              margin: isMobile ? 1 : 2,
              maxWidth: isMobile ? 'calc(100% - 16px)' : 'calc(100% - 32px)',
              '& .MuiTypography-root': {
                wordBreak: 'normal',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                whiteSpace: 'normal'
              },
              '& .MuiTypography-root[data-test*="dialog-title"]': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              },
              '& .MuiDialogContent-root': {
                padding: isMobile ? '16px' : '24px'
              },
              '& .MuiDialogActions-root': {
                padding: isMobile ? '8px 16px 16px' : '16px 24px 24px'
              },
              '& .MuiGrid-container': {
                flexWrap: 'nowrap',
                '& .MuiGrid-item:first-of-type': {
                  flexShrink: 0,
                  minWidth: 'auto'
                },
                '& .MuiGrid-item:last-of-type': {
                  flex: 1,
                  minWidth: 0
                }
              },
              '& .MuiGrid-item .MuiGrid-container': {
                flexWrap: 'nowrap'
              }
            }
          }
        }}
      />
    </Box>
  )
}

export default CustomSkillInput
